/**
 * Dashboard insights hook
 * Thin wrapper around data layer using React Query.
 * When startDate/endDate are provided, insights are computed for that period (and same-length previous period).
 */

import { useQuery } from '@tanstack/react-query';
import { dashboardKeys, queryOptions } from '@/data/query-keys';
import { fetchDashboardInsights } from '@/data/supabase/dashboard';
import { getCurrentUserCompanyId } from '@/lib/userUtils';

// Re-export types for backward compatibility
export type {
  StatusLevel,
  PriorityDecision,
  CashPosition,
  MonthlyExpense,
  SpendingBreakdown,
  SpendingCategory,
  PerformanceSnapshot,
  PlatformSales,
  ShiftPerformance,
  TaxLiabilities,
  MacroKPIs,
  ExpenseRatios,
  DashboardInsights,
} from '@/types/dashboard';

export interface UseDashboardInsightsOptions {
  startDate?: string | null;
  endDate?: string | null;
}

export const useDashboardInsights = (options?: UseDashboardInsightsOptions) => {
  const startDate = options?.startDate ?? null;
  const endDate = options?.endDate ?? null;

  return useQuery({
    queryKey: dashboardKeys.insights(startDate, endDate),
    queryFn: async () => {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return null;
      return fetchDashboardInsights(companyId, startDate && endDate ? { startDate, endDate } : null);
    },
    staleTime: queryOptions.dashboard.staleTime,
    gcTime: queryOptions.dashboard.gcTime,
  });
};
